import type { HHMM } from './time';

export type PrayerKey = 'fajr' | 'sunrise' | 'dhuhr' | 'asr' | 'maghrib' | 'isha';

export type PrayerDayLike = Record<PrayerKey, HHMM>;

/** Display order; sunrise is listed (it ends Fajr's time) but is not a prayer. */
export const PRAYER_ORDER: PrayerKey[] = ['fajr', 'sunrise', 'dhuhr', 'asr', 'maghrib', 'isha'];
export const PRAYERS: PrayerKey[] = ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'];

export function prayerName(key: PrayerKey, isFriday = false): string {
  if (key === 'dhuhr' && isFriday) return 'Jumuʿah';
  return { fajr: 'Fajr', sunrise: 'Sunrise', dhuhr: 'Dhuhr', asr: 'Asr', maghrib: 'Maghrib', isha: 'Isha' }[key];
}

const toMin = (t: HHMM) => {
  const [h, m] = t.split(':').map(Number) as [number, number];
  return h * 60 + m;
};

export interface SalatRow {
  key: PrayerKey;
  time: HHMM;
  state: 'past' | 'current' | 'next' | 'upcoming';
}

export interface SalatSchedule {
  rows: SalatRow[];
  /** The prayer whose time is running now (null before today's Fajr, or between sunrise and Dhuhr). */
  current: PrayerKey | null;
  next: { key: PrayerKey; time: HHMM; tomorrow: boolean; minutesUntil: number };
}

/**
 * Today's rows with state, the current prayer and the next one with a countdown. After Isha the
 * next prayer is tomorrow's Fajr. Between sunrise and Dhuhr no prayer is "current" (Fajr has ended).
 */
export function salatSchedule(today: PrayerDayLike, tomorrow: PrayerDayLike, now: HHMM): SalatSchedule {
  const n = toMin(now);
  let current: PrayerKey | null = null;
  for (const k of PRAYERS) if (toMin(today[k]) <= n) current = k;
  if (current === 'fajr' && toMin(today.sunrise) <= n) current = null;

  const upcoming = PRAYERS.find((k) => toMin(today[k]) > n);
  const next = upcoming
    ? { key: upcoming, time: today[upcoming], tomorrow: false, minutesUntil: toMin(today[upcoming]) - n }
    : { key: 'fajr' as PrayerKey, time: tomorrow.fajr, tomorrow: true, minutesUntil: 24 * 60 - n + toMin(tomorrow.fajr) };

  const rows: SalatRow[] = PRAYER_ORDER.map((key) => {
    const t = toMin(today[key]);
    let state: SalatRow['state'] = t <= n ? 'past' : 'upcoming';
    if (key === current) state = 'current';
    if (!next.tomorrow && key === next.key) state = 'next';
    return { key, time: today[key], state };
  });
  return { rows, current, next };
}

/** "in 45 min", "in 2 h 5 min", "in 1 h" */
export function countdownLabel(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  if (m < 60) return `in ${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `in ${h} h ${r} min` : `in ${h} h`;
}
